import type { CSSObject } from '@emotion/react';
import type { EmailTemplate } from '@weave/schema-gen-ts/dist/schemas/messaging/bulk/email-template/v1/emailtemplate.pb';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { BulkEmailPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { checkTemplateJustCreated } from '../../utils/check-template-just-created';
import { TemplateCardActions } from './template-card-actions';

type Props = {
  onDelete?: (templateId: string) => void;
  onDuplicate?: (template: EmailTemplate) => void;
  onTemplateClick: (isEditingTemplate?: boolean) => void;
  onView?: (template: EmailTemplate) => void;
  template: EmailTemplate;
};

export const BulkEmailTemplateCard = ({ onDelete, onDuplicate, onTemplateClick, onView, template }: Props) => {
  const { t } = useTranslation('bulk-messaging');

  const isJustCreated = checkTemplateJustCreated(template.createdAt, template.updatedAt);
  const dateLabel = isJustCreated
    ? t('Just Created')
    : template.updatedAt
    ? t('Last Used {{date}}', { date: formatDate(template.updatedAt, 'MMMM D, YYYY') })
    : '';

  return (
    <div css={cardContainerStyle}>
      <button
        css={cardStyle}
        onClick={() => onTemplateClick()}
        data-trackingid={`${BulkEmailPrefixes.Templates}-compose-card-btn`}
      >
        <div css={thumbnailAreaStyle}>
          <div css={thumbnailInnerStyle}>
            {template.thumbnailUrl ? (
              <img
                src={template.thumbnailUrl}
                alt={template.title ?? ''}
                css={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'top',
                }}
              />
            ) : (
              <div css={thumbnailPlaceholderStyle}>
                <Icon name='email-small' color='subdued' size={32} />
              </div>
            )}
          </div>
        </div>

        <div css={footerStyle} onClick={(e) => e.stopPropagation()}>
          <div css={footerContentStyle}>
            <Text weight='bold' css={{ lineHeight: theme.spacing(3), wordBreak: 'break-word' }}>
              {template.title}
            </Text>
            <Text color='subdued' size='small'>
              {dateLabel}
            </Text>
          </div>

          <TemplateCardActions
            template={template}
            onDelete={onDelete}
            onDuplicate={onDuplicate}
            onSelect={() => onTemplateClick()}
            onView={onView}
          />
        </div>
      </button>
    </div>
  );
};

const cardContainerStyle: CSSObject = {
  width: '100%',
  minWidth: 0,
  maxWidth: 340,
};

const cardStyle: CSSObject = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: 265,
  backgroundColor: theme.colors.white,
  border: `1px solid ${theme.colors.neutral20}`,
  borderRadius: theme.borderRadius.medium,
  overflow: 'hidden',
  cursor: 'pointer',
  textAlign: 'left',
  padding: 0,
  '&:hover': {
    boxShadow: theme.shadows.floating,
    borderColor: theme.colors.primary50,
  },
  transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
};

const thumbnailAreaStyle: CSSObject = {
  flex: 1,
  background: `linear-gradient(180deg, ${theme.colors.neutral5} 0%, rgba(244,245,247,0) 100%)`,
  padding: theme.spacing(1),
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 0,
};

const thumbnailInnerStyle: CSSObject = {
  position: 'relative',
  width: '100%',
  height: '100%',
  border: `1px solid ${theme.colors.neutral20}`,
  borderRadius: theme.borderRadius.small,
  overflow: 'hidden',
};

const thumbnailPlaceholderStyle: CSSObject = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.colors.neutral5,
};

const footerStyle: CSSObject = {
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2),
  borderTop: `1px solid ${theme.colors.neutral20}`,
  backgroundColor: theme.colors.white,
  gap: theme.spacing(1),
};

const footerContentStyle: CSSObject = {
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
};
